import { HAND_TO_TARGETS, WORKFLOW_LIMITS, type HandToTarget } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Field, Icon, Select } from "@sagot/ui";
import type { DisableReplyStep, EnableReplyStep, FacingStep, HandToPersonStep, LetAssistantAnswerStep, SendMessageStep } from "../types";
import { OptionSelect } from "./action-forms";
import { ArticleForm } from "./article-form";
import { ReplyButtonsForm } from "./buttons-editor";
import { ShowBookingForm, ShowLeadFormForm } from "./card-forms";
import { StepChannelNotes } from "./channel-notes";
import { CollectDataForm } from "./collect-form";
import { CollectReplyForm } from "./collect-reply-form";
import { CountedInput, CountedTextarea } from "./counted-field";
import { AskRatingForm } from "./rating-form";
import { VerifyEmailForm } from "./verify-email-form";
import { ExitsEditor } from "./exits-editor";
import type { FlowContext } from "./flow-context";
import { useRefs } from "./refs";
import { lockHasEffect } from "./reply-model";
import { VariableList, type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";

function SendMessageForm({ step, disabled, onChange }: FormProps<SendMessageStep>) {
  const { t } = useTranslation();
  return (
    <div>
      <CountedTextarea label={t(`${F}.visitorMessage`)} hint={t(`${F}.messageMarkdownHint`)} rows={5} value={step.text} max={WORKFLOW_LIMITS.message} disabled={disabled} onChange={(text) => { onChange({ ...step, text }); }} />
      <VariableList />
    </div>
  );
}

/** Let the assistant answer: optional guidance, and where each outcome of the next answer leads. */
function LetAssistantAnswerForm({ step, disabled, onChange, paths, canAddPath }: FormProps<LetAssistantAnswerStep> & Pick<FlowContext, "paths" | "canAddPath">) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-xs text-fg-subtle">{t(`${F}.answerIntro`)}</p>
      <CountedTextarea
        label={t(`${F}.guidance`)}
        hint={t(`${F}.guidanceHint`)}
        value={step.guidance ?? ""}
        max={WORKFLOW_LIMITS.guidance}
        disabled={disabled}
        onChange={(guidance) => { onChange({ ...step, guidance: guidance || undefined }); }}
      />
      <ExitsEditor step={step} onChange={onChange} paths={paths} canAddPath={canAddPath} disabled={disabled} />
    </div>
  );
}

/** Hand to a person: the default routing, a team or one member, and a note for whoever picks it up. */
function HandToPersonForm({ step, disabled, onChange }: FormProps<HandToPersonStep>) {
  const { t } = useTranslation();
  const { teams, members } = useRefs();
  return (
    <div className="space-y-4">
      <Field label={t(`${F}.handTo`)} hint={t(`${F}.handToHint`)}>
        <Select value={step.to} disabled={disabled} onChange={(e) => { onChange({ id: step.id, name: step.name, type: "hand_to_person", to: e.target.value as HandToTarget, reason: step.reason }); }}>
          {HAND_TO_TARGETS.map((to) => <option key={to} value={to}>{t(`${F}.handTargets.${to}`)}</option>)}
        </Select>
      </Field>
      {step.to === "team" ? <OptionSelect label={t(`${F}.team`)} value={step.team_id} options={teams} disabled={disabled} empty={t(`${F}.choose`)} onChange={(team_id) => { onChange({ ...step, team_id }); }} /> : null}
      {step.to === "user" ? <OptionSelect label={t(`${F}.member`)} value={step.user_id} options={members} disabled={disabled} empty={t(`${F}.choose`)} onChange={(user_id) => { onChange({ ...step, user_id }); }} /> : null}
      <CountedInput label={t(`${F}.handReason`)} hint={t(`${F}.handReasonHint`)} value={step.reason ?? ""} max={WORKFLOW_LIMITS.handReason} disabled={disabled} onChange={(reason) => { onChange({ ...step, reason: reason || undefined }); }} />
    </div>
  );
}

/** Disable / enable customer reply: no settings, only what the step does and where it works. */
function LockForm({ step, following }: { step: DisableReplyStep | EnableReplyStep; following: FlowContext["following"] }) {
  const { t } = useTranslation();
  const lock = step.type === "disable_reply";
  const idle = lock && following !== undefined && !lockHasEffect(following);
  return (
    <div className="space-y-4">
      <p className="text-sm text-fg-muted">{t(`${F}.${lock ? "lockHelp" : "unlockHelp"}`)}</p>
      {lock ? <p className="text-xs text-fg-subtle">{t(`${F}.lockLifts`)}</p> : null}
      {idle ? (
        <p className="flex items-start gap-2 rounded-md bg-warning-soft p-3 text-xs font-semibold text-warning" data-testid="lock-idle">
          <Icon name="alert-triangle" className="mt-0.5 size-3.5 shrink-0" />
          {t(`${F}.lockIdle`)}
        </p>
      ) : null}
      <StepChannelNotes type={step.type} />
    </div>
  );
}

/** The form for any customer-facing step. */
export function FacingForm({ step, disabled, onChange, ...flow }: FormProps<FacingStep> & FlowContext) {
  const { t } = useTranslation();
  switch (step.type) {
    case "send_message":
      return <SendMessageForm step={step} disabled={disabled} onChange={onChange} />;
    case "reply_buttons":
      return <ReplyButtonsForm step={step} disabled={disabled} onChange={onChange} paths={flow.paths} canAddPath={flow.canAddPath} pathId={flow.pathId} />;
    case "collect_data":
      return <CollectDataForm step={step} disabled={disabled} onChange={onChange} />;
    case "let_assistant_answer":
      return <LetAssistantAnswerForm step={step} disabled={disabled} onChange={onChange} paths={flow.paths} canAddPath={flow.canAddPath} />;
    case "show_booking":
      return <ShowBookingForm step={step} disabled={disabled} onChange={onChange} {...flow} />;
    case "show_lead_form":
      return <ShowLeadFormForm step={step} disabled={disabled} onChange={onChange} {...flow} />;
    case "hand_to_person":
      return <HandToPersonForm step={step} disabled={disabled} onChange={onChange} />;
    case "expected_reply_time":
      return <p className="text-sm text-fg-muted">{t(`${F}.replyTimeHelp`)}</p>;
    case "ask_rating":
      return <AskRatingForm step={step} disabled={disabled} onChange={onChange} paths={flow.paths} canAddPath={flow.canAddPath} />;
    case "collect_reply":
      return <CollectReplyForm step={step} disabled={disabled} onChange={onChange} paths={flow.paths} canAddPath={flow.canAddPath} />;
    case "verify_email":
      return <VerifyEmailForm step={step} disabled={disabled} onChange={onChange} paths={flow.paths} canAddPath={flow.canAddPath} />;
    case "disable_reply":
    case "enable_reply":
      return <LockForm step={step} following={flow.following} />;
    case "show_article":
      return <ArticleForm step={step} disabled={disabled} onChange={onChange} />;
  }
}
